import type { FC } from 'react';

import CheckmarkCircle from '@crehana/compass.icons.fill/CheckmarkCircle';
import CloseCircle from '@crehana/compass.icons.fill/CloseCircle';
import DownloadCircle from '@crehana/compass.icons.fill/DownloadCircle';
import Info from '@crehana/compass.icons.outline/Info';
import { useUITheme } from '@crehana/compass.ui';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Container } from '@crehana/ui';
import { clsx } from 'clsx';

import CardContainer from '@/views/AdminDashboard/components/CardContainer';

import { useExcelImporterStepperContext } from '../../context';
import { getErrorsToDisplay, getTotalNestedErrors } from '../../utils';
import { ErrorTables } from './components';

const COLORS = {
  bg: {
    success: 'tw-bg-success-100',
    alert: 'tw-bg-alert-200',
    info: 'tw-bg-info-200',
  },
  icon: {
    success: 'tw-text-success-500',
    alert: 'tw-text-alert-500',
    info: 'tw-text-info-500',
  },
};

const InfoContent: FC<{ type: 'success' | 'alert' | 'info'; text: string }> = ({
  type = 'alert',
  text,
}) => (
  <div
    className={clsx(
      'tw-flex tw-flex-row tw-gap-1 tw-justify-center tw-rounded-b-1 tw-py-1',
      COLORS.bg[type],
    )}
  >
    <Info
      size={16}
      className={clsx('tw-flex tw-self-center', COLORS.icon[type])}
    />
    <p className="tw-text-[12px] tw-self-center">{text}</p>
  </div>
);

//TODO: usersToUpdate should be configurable
const ErrorDetail: FC = () => {
  const { isDark } = useUITheme();
  const { t } = useTranslation();
  const {
    errors = [],
    validationMetadata,
    currentErrorRevision,
    hasNestedValidations,
    shouldShow,
  } = useExcelImporterStepperContext();

  const errorsToDisplay = getErrorsToDisplay(errors, currentErrorRevision);
  const totalErrors = getTotalNestedErrors(errorsToDisplay);

  const recordsToUpdate = validationMetadata?.usersToUpdate || [];
  const recordsToUpdateQty = (!!recordsToUpdate && recordsToUpdate.length) || 0;

  return (
    <div className="flex flex-col items-center pt-56 pb-20 w-full">
      {shouldShow(['step3', 'all']) && (
        <div className="tw-pb-4">
          <p className="tw-font-h4 dark:tw-text-neutral-light-100 tw-text-center">
            {t('STEP_2_TITLE_DETAIL')}
          </p>
          <p className="tw-font-body4 tw-text-neutral-light-700 whitespace-pre-line tw-text-center">
            {t('STEP_2_SUBTITLE_DETAIL')}
          </p>
        </div>
      )}
      <Container>
        {hasNestedValidations && (
          <>
            <CardContainer
              title={t('STEP_2_TABLE_DETAIL_TITLE_SUCCESS')}
              icon={
                <CheckmarkCircle className="tw-text-success-400" size={24} />
              }
              counterDirection="left"
              counter={validationMetadata?.totalValidRecords || 0}
              extendedClassName="tw-mb-2"
              isDark={isDark}
              isCompact
            >
              <InfoContent
                type="success"
                text={t('DETAIL_ACTIVATION_NOTICE')}
              />
            </CardContainer>
            {recordsToUpdateQty > 0 && (
              <>
                <CardContainer
                  title={t('STEP_2_TABLE_DETAIL_TITLE_UPDATE')}
                  icon={
                    <DownloadCircle
                      className="tw-text-info-500 tw-rotate-180"
                      size={24}
                    />
                  }
                  counterDirection="left"
                  counter={recordsToUpdateQty}
                  extendedClassName="tw-mb-2"
                  isDark={isDark}
                  isCompact
                >
                  <InfoContent type="info" text={t('DETAIL_UPDATE_NOTICE')} />
                </CardContainer>
                <ErrorTables
                  hasNestedValidations={hasNestedValidations}
                  errors={recordsToUpdate || []}
                  type="info"
                />
              </>
            )}
            <CardContainer
              title={t('STEP_2_TABLE_DETAIL_TITLE')}
              icon={<CloseCircle className="tw-text-alert-400" size={24} />}
              counterDirection="left"
              counter={totalErrors || 0}
              extendedClassName="tw-mb-2"
              isDark={isDark}
              isCompact
            >
              <InfoContent type="alert" text={t('DETAIL_ERROR_NOTICE')} />
            </CardContainer>
          </>
        )}
        {currentErrorRevision && (
          <ErrorTables
            key={currentErrorRevision.toString()}
            hasNestedValidations={hasNestedValidations}
            errors={errorsToDisplay}
          />
        )}
      </Container>
    </div>
  );
};

export default ErrorDetail;
